/**
 * Shared SegmentHeader — matches the frontend's SegmentDashboards header exactly.
 * Breadcrumbs → Tabs → Header card (avatar | name + subtitle + tags | sample size)
 */

import { useCallback, useState } from 'react';
import { styled, Box } from '@mui/material';
import { SegmentAvatar } from '../SegmentAvatar/index.js';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface SegmentHeaderTab {
  key: string;
  title: string;
}

export interface SegmentHeaderSampleData {
  segmentN: number;
  totalN: number;
}

export interface SegmentHeaderProps {
  name: string;
  subtitle?: string;
  attributes?: string[];
  iconUrl?: string;
  sampleData?: SegmentHeaderSampleData | null;
  tabs: SegmentHeaderTab[];
  activeTabIndex: number;
  onTabChange: (index: number) => void;
  onChatWithSegment?: () => void;
  breadcrumbs?: React.ReactNode;
  primaryColor?: string;
  published?: boolean;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function SegmentHeader({
  name,
  subtitle,
  attributes,
  iconUrl,
  sampleData,
  tabs,
  activeTabIndex,
  onTabChange,
  onChatWithSegment,
  breadcrumbs,
  primaryColor,
}: SegmentHeaderProps): React.ReactElement {
  const accentColor = primaryColor ?? 'var(--pri-02, #28AAE1)';

  return (
    <Root>
      {breadcrumbs && <BreadcrumbsSlot>{breadcrumbs}</BreadcrumbsSlot>}

      {/* Tab bar — same styling as shared Tabs component / frontend TabBar */}
      <TabList>
        {tabs.map((tab, i) => (
          <TabButton
            key={tab.key}
            $active={i === activeTabIndex}
            $accentColor={accentColor}
            onClick={() => onTabChange(i)}
          >
            {tab.title}
          </TabButton>
        ))}
      </TabList>

      {activeTabIndex === 0 && (
        <HeaderCard>
          {iconUrl && (
            <AvatarWrap>
              <SegmentAvatar
                iconUrl={iconUrl}
                name={name}
                onChatWithSegment={onChatWithSegment}
                primaryColor={primaryColor}
              />
            </AvatarWrap>
          )}

          <InfoSection>
            <SegmentName>{name}</SegmentName>
            {subtitle && <SubtitleText>{subtitle}</SubtitleText>}
            {attributes && attributes.length > 0 && (
              <TagRow>
                {attributes.map((tag) => (
                  <AttributeTag key={tag}>{tag}</AttributeTag>
                ))}
              </TagRow>
            )}
          </InfoSection>

          {sampleData && (
            <SampleSizeBox>
              <SampleLabel>Sample Size</SampleLabel>
              <SampleValue>
                {sampleData.segmentN.toLocaleString()} / {sampleData.totalN.toLocaleString()}
              </SampleValue>
            </SampleSizeBox>
          )}
        </HeaderCard>
      )}
    </Root>
  );
}

// ─── Styled — matches frontend Segment.Heading + TabBar exactly ─────────────

const Root = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
});

const BreadcrumbsSlot = styled(Box)({
  padding: '8px 0',
  alignSelf: 'flex-start',
});

const TabList = styled('div')({
  display: 'flex',
  position: 'relative',
  backgroundColor: 'var(--pri-01, #ffffff)',
  borderBottom: '3px solid var(--sec-01, #e5e5e5)',
});

const TabButton = styled('button')<{ $active: boolean; $accentColor: string }>(({ $active, $accentColor }) => ({
  display: 'inline-flex',
  width: '150px',
  fontSize: '16px',
  lineHeight: '45px',
  minHeight: '45px',
  textAlign: 'center' as const,
  justifyContent: 'center',
  fontFamily: 'var(--font-bold, inherit)',
  fontWeight: 'bold',
  color: $active ? 'var(--hyperlink, #28AAE1)' : 'var(--gray-d, #626262)',
  cursor: 'pointer',
  background: 'transparent',
  border: 'none',
  position: 'relative' as const,
  padding: 0,
  '&::after': {
    content: '""',
    position: 'absolute' as const,
    bottom: '-3px',
    left: 0,
    right: 0,
    height: '4px',
    backgroundColor: $active ? 'var(--hyperlink, #28AAE1)' : 'transparent',
    transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    zIndex: 1,
  },
  '&:hover': {
    color: 'var(--hyperlink, #28AAE1)',
  },
  '&:focus': {
    outline: 'none',
  },
}));

const HeaderCard = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  padding: '16px',
  paddingBottom: '24px',
  background: 'var(--pri-01, #ffffff)',
  borderRadius: '8px',
  marginTop: '8px',
  marginBottom: '16px',
  border: '1px solid var(--sec-01, #e5e5e5)',
  overflow: 'visible',
  gap: '16px',
});

const AvatarWrap = styled(Box)({
  flexShrink: 0,
  marginLeft: '16px',
  marginRight: '24px',
  marginBottom: '16px',
});

const InfoSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  flex: 1,
  minHeight: '100px',
});

const SegmentName = styled('h2')({
  fontSize: '26px',
  fontWeight: 700,
  margin: 0,
  color: 'var(--black, #323232)',
});

const SubtitleText = styled('div')({
  fontSize: '16px',
  color: 'var(--black, #323232)',
});

const TagRow = styled(Box)({
  display: 'flex',
  gap: '8px',
  marginTop: 'auto',
  flexWrap: 'wrap',
});

const AttributeTag = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  height: '32px',
  color: 'var(--black, #323232)',
  backgroundColor: 'var(--gray-xl, #f5f5f5)',
  border: '1px solid var(--gray-l, #e5e5e5)',
  borderRadius: '16px',
  padding: '0 12px',
  fontSize: '13px',
  fontWeight: 500,
  fontFamily: 'var(--font-semibold, inherit)',
});

const SampleSizeBox = styled(Box)({
  borderRadius: '8px',
  flexShrink: 0,
  backgroundColor: 'var(--gray-light2, #f5f5f5)',
  padding: '16px',
  minWidth: '180px',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});

const SampleLabel = styled('div')({
  fontSize: '14px',
  color: 'var(--gray-d, #626262)',
  margin: 0,
});

const SampleValue = styled('div')({
  fontSize: '14px',
  fontWeight: 700,
  color: 'var(--black, #323232)',
});
